/**
 * components/charts/DrawdownChart — the equity "underwater" plot: how far below its
 * running peak the account sat at each point, as a percentage. Drawdown is a one-sided
 * magnitude (it is 0 at a fresh high and only ever dips), so this is a single loss-red
 * area hanging from the 0% waterline — no second hue, no legend box (the caption names
 * it). The worst point is marked selectively. Pure presentational: it takes the derived
 * drawdown series and renders SVG.
 */
import { useMemo } from 'react';

import { pctPlain } from '../../lib/format';
import type { DrawdownPoint } from '../../lib/analytics';

interface DrawdownChartProps {
  readonly points: readonly DrawdownPoint[];
  readonly maxDrawdown: number;
  readonly height?: number;
}

const VIEW_W = 720;
const PAD = { l: 44, r: 14, t: 14, b: 24 } as const;

export function DrawdownChart({ points, maxDrawdown, height = 180 }: DrawdownChartProps): JSX.Element {
  const geo = useMemo(() => {
    if (points.length < 2) return null;
    const plotW = VIEW_W - PAD.l - PAD.r;
    const plotH = height - PAD.t - PAD.b;
    const ts = points.map((p) => p.at);
    const minT = Math.min(...ts);
    const maxT = Math.max(...ts);
    const floor = Math.min(-0.01, maxDrawdown) * 1.12; // negative
    const x = (t: number): number => PAD.l + ((t - minT) / (maxT - minT || 1)) * plotW;
    const y = (d: number): number => PAD.t + (d / floor) * plotH; // 0 → top, floor → bottom
    const xy = points.map((p) => ({ px: x(p.at), py: y(p.dd), ...p }));
    const line = xy.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.px.toFixed(1)} ${p.py.toFixed(1)}`).join(' ');
    const area = `${line} L${xy[xy.length - 1]!.px.toFixed(1)} ${PAD.t} L${xy[0]!.px.toFixed(1)} ${PAD.t} Z`;
    const worst = xy.reduce((a, p) => (p.dd < a.dd ? p : a), xy[0]!);
    const ticks = [0, floor / 2, floor];
    return { plotW, plotH, x, y, line, area, worst, ticks, minT, maxT };
  }, [points, maxDrawdown, height]);

  if (!geo) return <div className="state"><span className="muted">Not enough history to plot drawdown.</span></div>;

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${VIEW_W} ${height}`} role="img" aria-label="Equity drawdown from running peak" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ddFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--loss)" stopOpacity="0" />
            <stop offset="100%" stopColor="var(--loss)" stopOpacity="0.30" />
          </linearGradient>
        </defs>
        {geo.ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.l} x2={VIEW_W - PAD.r} y1={geo.y(t)} y2={geo.y(t)} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={PAD.l - 6} y={geo.y(t) + 3} fontSize={11} fill="var(--text-muted)" textAnchor="end" fontFamily="var(--font-mono)">
              {t === 0 ? '0%' : `−${pctPlain(Math.abs(t), 1)}`}
            </text>
          </g>
        ))}
        <path d={geo.area} fill="url(#ddFill)" />
        <path d={geo.line} fill="none" stroke="var(--loss)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={geo.worst.px} cy={geo.worst.py} r={4} fill="var(--loss)" stroke="var(--viz-surface)" strokeWidth={2}>
          <title>{`Max drawdown −${pctPlain(Math.abs(geo.worst.dd), 2)}`}</title>
        </circle>
      </svg>
      <div className="chart__caption">
        <span className="chart__legend-swatch" style={{ background: 'var(--loss)' }} aria-hidden />
        <span>Drawdown from peak</span>
        <span style={{ marginLeft: 'auto' }} className="num loss">max −{pctPlain(Math.abs(maxDrawdown), 2)}</span>
      </div>
    </div>
  );
}
